import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

// Types
type LevelId = 'junior' | 'mid' | 'senior' | 'staff' | 'principal'

interface Level {
  id: LevelId
  label: string
  shortLabel: string
}

const LEVELS: Level[] = [
  { id: 'junior', label: 'Junior', shortLabel: 'Jr' },
  { id: 'mid', label: 'Mid', shortLabel: 'Mid' },
  { id: 'senior', label: 'Senior', shortLabel: 'Sr' },
  { id: 'staff', label: 'Staff', shortLabel: 'Staff' },
  { id: 'principal', label: 'Principal', shortLabel: 'Princ' },
]

const JOB_FAMILIES = [
  'Engineering',
  'Product',
  'Design',
  'Data',
  'Operations',
  'Sales',
  'Marketing',
  'Finance',
  'People',
  'Other',
]

const LOCATION_PRESETS = [
  'Remote',
  'San Francisco',
  'New York',
  'London',
  'Berlin',
  'Toronto',
  'Singapore',
  'Austin',
  'Seattle',
]

interface HiringPrinciple {
  id: string
  name: string
  description: string
  enabled: boolean
}

const DEFAULT_PRINCIPLES: Omit<HiringPrinciple, 'id'>[] = [
  { name: 'Ownership', description: 'Takes end-to-end responsibility', enabled: true },
  { name: 'Judgment', description: 'Makes sound decisions with incomplete data', enabled: true },
  { name: 'Communication', description: 'Explains complex ideas clearly', enabled: false },
  { name: 'Collaboration', description: 'Elevates team performance', enabled: false },
  { name: 'Craft', description: 'Cares deeply about quality of work', enabled: false },
  { name: 'Growth', description: 'Learns quickly and seeks feedback', enabled: false },
]

interface LocationSalaryOverride {
  location: string
  min: string
  max: string
}

// Icons
function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}

function CurrencyDollarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

// Generate unique ID
function generateId() {
  return Math.random().toString(36).substring(2, 9)
}

export default function CreateRolePage() {
  const navigate = useNavigate()

  // Core required fields
  const [title, setTitle] = useState('')
  const [jobFamily, setJobFamily] = useState('')
  const [levelMin, setLevelMin] = useState<LevelId>('mid')
  const [levelMax, setLevelMax] = useState<LevelId>('senior')
  const [locations, setLocations] = useState<string[]>([])
  const [principles, setPrinciples] = useState<HiringPrinciple[]>(
    DEFAULT_PRINCIPLES.map((p) => ({ ...p, id: generateId() }))
  )

  // Salary fields (always visible)
  const [defaultSalaryMin, setDefaultSalaryMin] = useState('')
  const [defaultSalaryMax, setDefaultSalaryMax] = useState('')
  const [showSalaryOverrides, setShowSalaryOverrides] = useState(false)
  const [salaryOverrides, setSalaryOverrides] = useState<LocationSalaryOverride[]>([])

  // Optional fields
  const [showOptional, setShowOptional] = useState(false)
  const [barRaiserRequired, setBarRaiserRequired] = useState(false)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Validation
  const enabledPrinciples = principles.filter((p) => p.enabled)
  const isValid = useMemo(() => {
    return (
      title.trim().length > 0 &&
      jobFamily.length > 0 &&
      locations.length > 0 &&
      enabledPrinciples.length >= 2
    )
  }, [title, jobFamily, locations, enabledPrinciples])

  // Level range display
  const levelRangeDisplay = useMemo(() => {
    const minIndex = LEVELS.findIndex((l) => l.id === levelMin)
    const maxIndex = LEVELS.findIndex((l) => l.id === levelMax)
    if (minIndex === maxIndex) {
      return LEVELS[minIndex].label
    }
    return `${LEVELS[minIndex].label} → ${LEVELS[maxIndex].label}`
  }, [levelMin, levelMax])

  // Handlers
  const toggleLocation = (loc: string) => {
    setLocations((prev) =>
      prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]
    )
  }

  const togglePrinciple = (id: string) => {
    setPrinciples((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p))
    )
  }

  const handleLevelChange = (type: 'min' | 'max', value: LevelId) => {
    const minIndex = LEVELS.findIndex((l) => l.id === (type === 'min' ? value : levelMin))
    const maxIndex = LEVELS.findIndex((l) => l.id === (type === 'max' ? value : levelMax))

    if (type === 'min') {
      setLevelMin(value)
      if (minIndex > maxIndex) {
        setLevelMax(value)
      }
    } else {
      setLevelMax(value)
      if (maxIndex < minIndex) {
        setLevelMin(value)
      }
    }
  }

  // When user enables overrides, pre-populate with all selected locations
  const enableSalaryOverrides = () => {
    setShowSalaryOverrides(true)
    // Pre-populate with all locations that don't already have overrides
    const existingLocations = salaryOverrides.map((o) => o.location)
    const newOverrides = locations
      .filter((loc) => !existingLocations.includes(loc))
      .map((location) => ({ location, min: '', max: '' }))
    setSalaryOverrides((prev) => [...prev, ...newOverrides])
  }

  const updateSalaryOverride = (location: string, field: 'min' | 'max', value: string) => {
    setSalaryOverrides((prev) =>
      prev.map((o) => (o.location === location ? { ...o, [field]: value } : o))
    )
  }

  const clearSalaryOverride = (location: string) => {
    setSalaryOverrides((prev) =>
      prev.map((o) => (o.location === location ? { ...o, min: '', max: '' } : o))
    )
  }

  const handleSubmit = async () => {
    if (!isValid) return
    
    setIsSubmitting(true)
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800))
    
    // Navigate to success page with role data
    navigate('/roles/new/ready', {
      state: {
        role: {
          title,
          jobFamily,
          levelMin,
          levelMax,
          locations,
          principles: enabledPrinciples,
          defaultSalary: { min: defaultSalaryMin, max: defaultSalaryMax },
          salaryOverrides: salaryOverrides.filter((o) => o.min || o.max),
          barRaiserRequired,
        },
      },
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-brand-50/20">
      {/* Ambient background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-brand-100/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-emerald-50/40 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-surface-200/60 bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5 group">
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8 transition-transform group-hover:scale-105" />
            <span className="font-display font-semibold text-lg text-surface-900">Jumpstone</span>
          </Link>
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 text-sm text-surface-500 hover:text-surface-700 transition-colors"
          >
            Cancel
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-surface-900 mb-3">
            Define a decision context
          </h1>
          <p className="text-lg text-surface-500">
            Not a job post — a frame for evaluating candidates.
          </p>
        </div>

        {/* Form sections */}
        <div className="space-y-8">
          {/* Section 1: Role Basics */}
          <section className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
                <BriefcaseIcon className="w-5 h-5 text-brand-600" />
              </div>
              <div>
                <h2 className="font-semibold text-surface-900">Role basics</h2>
                <p className="text-sm text-surface-500">What you're evaluating for</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Role title */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">
                  Role title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Backend Engineer"
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all text-surface-900 placeholder:text-surface-400"
                />
              </div>

              {/* Job family */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">
                  Job family
                </label>
                <div className="flex flex-wrap gap-2">
                  {JOB_FAMILIES.map((family) => (
                    <button
                      key={family}
                      onClick={() => setJobFamily(family)}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
                        ${jobFamily === family
                          ? 'bg-brand-600 border-brand-600 text-white shadow-sm'
                          : 'bg-white border-surface-200 text-surface-600 hover:border-surface-300 hover:bg-surface-50'
                        }
                      `}
                    >
                      {family}
                    </button>
                  ))}
                </div>
              </div>

              {/* Level range */}
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-2">
                  Level range
                </label>
                <div className="flex items-center gap-3">
                  <select
                    value={levelMin}
                    onChange={(e) => handleLevelChange('min', e.target.value as LevelId)}
                    className="flex-1 px-4 py-3 bg-white border border-surface-200 rounded-xl focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all text-surface-900 cursor-pointer"
                  >
                    {LEVELS.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-surface-400 font-medium">→</span>
                  <select
                    value={levelMax}
                    onChange={(e) => handleLevelChange('max', e.target.value as LevelId)}
                    className="flex-1 px-4 py-3 bg-white border border-surface-200 rounded-xl focus:border-brand-400 focus:ring-2 focus:ring-brand-100 focus:outline-none transition-all text-surface-900 cursor-pointer"
                  >
                    {LEVELS.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-2 text-sm text-surface-500">
                  Evaluating at: <span className="font-medium text-surface-700">{levelRangeDisplay}</span>
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Locations */}
          <section className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <MapPinIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-semibold text-surface-900">Location(s)</h2>
                <p className="text-sm text-surface-500">Where candidates can be based</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {LOCATION_PRESETS.map((loc) => {
                const isSelected = locations.includes(loc)
                return (
                  <button
                    key={loc}
                    onClick={() => toggleLocation(loc)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border inline-flex items-center gap-1.5
                      ${isSelected
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-surface-200 text-surface-600 hover:border-surface-300'
                      }
                    `}
                  >
                    {isSelected && <CheckIcon className="w-4 h-4" />}
                    {loc}
                  </button>
                )
              })}
            </div>

            {locations.length === 0 && (
              <p className="mt-3 text-sm text-amber-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                Select at least one location
              </p>
            )}
          </section>

          {/* Section 3: Hiring Principles */}
          <section className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-surface-900">Hiring principles to index on</h2>
                <p className="text-sm text-surface-500">What matters most for this role (select 2+)</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {principles.map((principle) => (
                <button
                  key={principle.id}
                  onClick={() => togglePrinciple(principle.id)}
                  className={`
                    p-4 rounded-xl border-2 text-left transition-all duration-200
                    ${principle.enabled
                      ? 'border-amber-300 bg-amber-50/50'
                      : 'border-surface-200 bg-surface-50/50 hover:border-surface-300'
                    }
                  `}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`font-medium ${principle.enabled ? 'text-surface-900' : 'text-surface-600'}`}>
                        {principle.name}
                      </p>
                      <p className="text-sm text-surface-500 mt-0.5">
                        {principle.description}
                      </p>
                    </div>
                    <div
                      className={`
                        w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors
                        ${principle.enabled
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'border-surface-300 bg-white'
                        }
                      `}
                    >
                      {principle.enabled && <CheckIcon className="w-3 h-3" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {enabledPrinciples.length < 2 && (
              <p className="mt-4 text-sm text-amber-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                Select at least 2 principles to evaluate against
              </p>
            )}
          </section>

          {/* Section 4: Salary Range (always visible) */}
          <section className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                <CurrencyDollarIcon className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <h2 className="font-semibold text-surface-900">Salary range</h2>
                <p className="text-sm text-surface-500">Applies to all locations by default</p>
              </div>
            </div>

            {/* Default salary range - always visible */}
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-surface-500 mb-1.5">Min</label>
                <input
                  type="text"
                  value={defaultSalaryMin}
                  onChange={(e) => setDefaultSalaryMin(e.target.value)}
                  placeholder="e.g. 120k"
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all text-surface-900 placeholder:text-surface-400"
                />
              </div>
              <span className="text-surface-400 font-medium mt-6">—</span>
              <div className="flex-1">
                <label className="block text-xs font-medium text-surface-500 mb-1.5">Max</label>
                <input
                  type="text"
                  value={defaultSalaryMax}
                  onChange={(e) => setDefaultSalaryMax(e.target.value)}
                  placeholder="e.g. 180k"
                  className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all text-surface-900 placeholder:text-surface-400"
                />
              </div>
            </div>

            {/* Location-specific overrides */}
            {locations.length > 0 && (
              <div className="mt-5 pt-5 border-t border-surface-100">
                {!showSalaryOverrides ? (
                  <button
                    onClick={enableSalaryOverrides}
                    className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-700 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    <span>Location-specific salary overrides</span>
                    <span className="text-surface-400">(optional)</span>
                  </button>
                ) : (
                  <div className="space-y-4 animate-fade-in">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-surface-700">Location-specific overrides</p>
                      <button
                        onClick={() => setShowSalaryOverrides(false)}
                        className="text-xs text-surface-400 hover:text-surface-600 transition-colors"
                      >
                        Use default for all
                      </button>
                    </div>

                    {/* Pre-populated location rows */}
                    <div className="space-y-3">
                      {salaryOverrides.map((override) => {
                        const hasOverride = override.min || override.max
                        return (
                          <div
                            key={override.location}
                            className={`
                              p-4 rounded-xl border transition-colors
                              ${hasOverride
                                ? 'bg-cyan-50/50 border-cyan-200'
                                : 'bg-surface-50 border-surface-200'
                              }
                            `}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="font-medium text-surface-900">{override.location}</span>
                              {hasOverride ? (
                                <button
                                  onClick={() => clearSalaryOverride(override.location)}
                                  className="text-xs text-surface-400 hover:text-surface-600 transition-colors"
                                >
                                  Use default
                                </button>
                              ) : (
                                <span className="text-xs text-surface-400">
                                  Using default range
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={override.min}
                                  onChange={(e) => updateSalaryOverride(override.location, 'min', e.target.value)}
                                  placeholder={defaultSalaryMin || 'Min'}
                                  className="w-full px-3 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all text-surface-900 placeholder:text-surface-400"
                                />
                              </div>
                              <span className="text-surface-400">—</span>
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={override.max}
                                  onChange={(e) => updateSalaryOverride(override.location, 'max', e.target.value)}
                                  placeholder={defaultSalaryMax || 'Max'}
                                  className="w-full px-3 py-2.5 bg-white border border-surface-200 rounded-lg text-sm focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all text-surface-900 placeholder:text-surface-400"
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <p className="text-xs text-surface-400">
                      Leave blank to inherit the default range above.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Optional section toggle */}
          <button
            onClick={() => setShowOptional(!showOptional)}
            className="w-full flex items-center justify-center gap-2 py-3 text-surface-500 hover:text-surface-700 transition-colors"
          >
            <span className="text-sm font-medium">
              {showOptional ? 'Hide optional settings' : 'Show optional settings'}
            </span>
            {showOptional ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>

          {/* Optional sections */}
          {showOptional && (
            <div className="space-y-6 animate-fade-in">
              {/* Bar-raiser */}
              <section className="bg-white rounded-2xl border border-surface-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                      <ShieldCheckIcon className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-surface-900">Bar-raiser required</h2>
                      <p className="text-sm text-surface-500">Require independent review before decisions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setBarRaiserRequired(!barRaiserRequired)}
                    className={`
                      relative w-12 h-7 rounded-full transition-colors duration-200
                      ${barRaiserRequired ? 'bg-violet-600' : 'bg-surface-200'}
                    `}
                  >
                    <span
                      className={`
                        absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
                        ${barRaiserRequired ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>

        {/* Submit button */}
        <div className="mt-10 pt-6 border-t border-surface-200">
          <button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className={`
              w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2
              ${isValid && !isSubmitting
                ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-500/25'
                : 'bg-surface-200 text-surface-400 cursor-not-allowed'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating role...
              </>
            ) : (
              <>
                Create role
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                </svg>
              </>
            )}
          </button>

          <p className="mt-4 text-center text-sm text-surface-500">
            This defines your evaluation frame. You can refine it later.
          </p>
        </div>
      </main>
    </div>
  )
}

